import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import clsx from "clsx";

import "./LuxFilterBar.css";

import { PRESET_RANGES, SORT_OPTIONS } from "./shopFilterOptions";
import {
  findPresetRange,
  getSortLabel,
  normalizeList,
} from "./shopFilterHelpers";

const FACET_FILTERS = [
  { key: "colors", label: "Color", title: "Select Color" },
  { key: "sizes", label: "Size", title: "Select Size" },
  { key: "materials", label: "Material", title: "Select Material" },
  { key: "patterns", label: "Pattern", title: "Select Pattern" },
];

const Chevron = ({ open = false }) => (
  <span className={clsx("lux-filter-bar__chevron", { "is-open": open })}>
    <svg viewBox="0 0 12 8" aria-hidden="true">
      <path
        d="M1 1.5L6 6.5L11 1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

function useOutsideClick(ref, handler) {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) return;
      handler();
    };

    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);

    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

function normalizePriceDraftValue(value) {
  const text = String(value ?? "").trim();
  if (!text) return "";

  const parsed = Number(text);
  if (!Number.isFinite(parsed) || parsed < 0) return "";

  return String(parsed);
}

function getFacetOptions(facets, filters, key) {
  return normalizeList([...(facets?.[key] || []), ...(filters?.[key] || [])]);
}

function hasFacetOptions(facets, filters, key) {
  return getFacetOptions(facets, filters, key).length > 0;
}

export default function LuxFilterBar({
  filters,
  facets,
  onFilterChange,
  onToggleFacetValue,
  onClearAll,
  onBulkFilterChange,
}) {
  const rootRef = useRef(null);

  const [openMenu, setOpenMenu] = useState(null);
  const [priceDraft, setPriceDraft] = useState({
    minPrice: filters.minPrice || "",
    maxPrice: filters.maxPrice || "",
  });

  const closePanel = useCallback(() => setOpenMenu(null), []);

  useOutsideClick(rootRef, closePanel);

  useEffect(() => {
    setPriceDraft({
      minPrice: filters.minPrice || "",
      maxPrice: filters.maxPrice || "",
    });
  }, [filters.minPrice, filters.maxPrice]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") closePanel();
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [closePanel]);

  const applyFilterChanges = useCallback(
    (updates = {}) => {
      if (typeof onBulkFilterChange === "function") {
        onBulkFilterChange(updates);
        return;
      }

      Object.entries(updates).forEach(([key, value]) => {
        onFilterChange(key, value);
      });
    },
    [onBulkFilterChange, onFilterChange],
  );

  const selectedSort =
    SORT_OPTIONS.find((option) => option.value === filters.sort) ||
    SORT_OPTIONS[0];

  const selectedPricePreset = findPresetRange(
    filters.minPrice,
    filters.maxPrice,
    PRESET_RANGES,
  );

  const visibleFacetFilters = useMemo(
    () =>
      FACET_FILTERS.filter(({ key }) => hasFacetOptions(facets, filters, key)),
    [facets, filters],
  );

  const activeFilters = useMemo(() => {
    const pills = [];

    FACET_FILTERS.forEach(({ key, label }) => {
      (filters[key] || []).forEach((value) => {
        pills.push({
          key: `${key}-${value}`,
          label: `${label}: ${value}`,
          onRemove: () => onToggleFacetValue(key, value),
        });
      });
    });

    if (filters.minPrice || filters.maxPrice) {
      const matchedPriceRange = findPresetRange(
        filters.minPrice,
        filters.maxPrice,
        PRESET_RANGES,
      );

      pills.push({
        key: "price",
        label: matchedPriceRange
          ? `Price: ${matchedPriceRange.label}`
          : `Price: £${filters.minPrice || "0"} – £${filters.maxPrice || "∞"}`,
        onRemove: () =>
          applyFilterChanges({
            minPrice: "",
            maxPrice: "",
          }),
      });
    }

    if (filters.inStock) {
      pills.push({
        key: "inStock",
        label: "In stock",
        onRemove: () => onFilterChange("inStock", false),
      });
    }

    if (filters.sort && filters.sort !== "newest") {
      pills.push({
        key: "sort",
        label: getSortLabel(filters.sort, SORT_OPTIONS),
        onRemove: () => onFilterChange("sort", "newest"),
      });
    }

    return pills;
  }, [applyFilterChanges, filters, onFilterChange, onToggleFacetValue]);

  const railFilters = useMemo(
    () => [
      ...visibleFacetFilters.map((item) => ({
        ...item,
        active: Boolean(filters[item.key]?.length),
        count: filters[item.key]?.length || 0,
      })),
      {
        key: "price",
        label: "Price",
        active: Boolean(filters.minPrice || filters.maxPrice),
        count: filters.minPrice || filters.maxPrice ? 1 : 0,
      },
    ],
    [filters, visibleFacetFilters],
  );

  const toggleMenu = useCallback((key) => {
    setOpenMenu((prev) => (prev === key ? null : key));
  }, []);

  const clearFacet = useCallback(
    (facetKey) => {
      (filters[facetKey] || []).forEach((item) =>
        onToggleFacetValue(facetKey, item),
      );
      closePanel();
    },
    [closePanel, filters, onToggleFacetValue],
  );

  const clearPrice = useCallback(() => {
    setPriceDraft({ minPrice: "", maxPrice: "" });

    applyFilterChanges({
      minPrice: "",
      maxPrice: "",
    });

    closePanel();
  }, [applyFilterChanges, closePanel]);

  const handlePriceApply = useCallback(() => {
    let nextMin = normalizePriceDraftValue(priceDraft.minPrice);
    let nextMax = normalizePriceDraftValue(priceDraft.maxPrice);

    if (nextMin && nextMax && Number(nextMin) > Number(nextMax)) {
      [nextMin, nextMax] = [nextMax, nextMin];
    }

    setPriceDraft({
      minPrice: nextMin,
      maxPrice: nextMax,
    });

    applyFilterChanges({
      minPrice: nextMin,
      maxPrice: nextMax,
    });

    closePanel();
  }, [
    applyFilterChanges,
    closePanel,
    priceDraft.maxPrice,
    priceDraft.minPrice,
  ]);

  const renderFacetMenu = (facetKey, title) => {
    const items = getFacetOptions(facets, filters, facetKey);
    const selectedValues = filters[facetKey] || [];

    return (
      <div className="lux-filter-bar__menu lux-filter-bar__menu--facet">
        <div className="lux-filter-bar__menu-header">
          <span className="lux-filter-bar__eyebrow">Refine by</span>
          <strong>{title}</strong>
        </div>

        {items.length ? (
          <div className="lux-filter-bar__check-list">
            {items.map((item) => {
              const checked = selectedValues.includes(item);

              return (
                <label key={item} className="lux-filter-bar__check-item">
                  <input
                    type="checkbox"
                    checked={Boolean(checked)}
                    onChange={() => onToggleFacetValue(facetKey, item)}
                  />
                  <span>{item}</span>
                </label>
              );
            })}
          </div>
        ) : (
          <div className="lux-filter-bar__menu-empty">
            No options available.
          </div>
        )}

        {selectedValues.length > 0 ? (
          <div className="lux-filter-bar__menu-footer">
            <button
              type="button"
              className="lux-filter-bar__footer-link"
              onClick={() => clearFacet(facetKey)}
            >
              Clear selection
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  const renderPriceMenu = () => (
    <div className="lux-filter-bar__menu lux-filter-bar__menu--price">
      <div className="lux-filter-bar__price-header">
        <div>
          <span className="lux-filter-bar__eyebrow">Price range</span>
          <div className="lux-filter-bar__price-summary">
            <span className="lux-filter-bar__price-currency">£</span>
            <strong>
              £{priceDraft.minPrice || "0"} – £{priceDraft.maxPrice || "∞"}
            </strong>
          </div>
        </div>

        <button
          type="button"
          className="lux-filter-bar__icon-close"
          onClick={closePanel}
          aria-label="Close price filter"
        >
          ×
        </button>
      </div>

      <div className="lux-filter-bar__price-box">
        <div className="lux-filter-bar__preset-row">
          {PRESET_RANGES.map((range) => {
            const active =
              selectedPricePreset?.min === range.min &&
              selectedPricePreset?.max === range.max;

            return (
              <button
                key={range.label}
                type="button"
                className={clsx("lux-filter-bar__preset-chip", {
                  "is-active": active,
                })}
                onClick={() => {
                  const nextMin = String(range.min);
                  const nextMax = range.max == null ? "" : String(range.max);

                  setPriceDraft({
                    minPrice: nextMin,
                    maxPrice: nextMax,
                  });

                  applyFilterChanges({
                    minPrice: nextMin,
                    maxPrice: nextMax,
                  });

                  closePanel();
                }}
              >
                {range.label}
              </button>
            );
          })}
        </div>

        <div className="lux-filter-bar__price-grid">
          <label className="lux-filter-bar__field">
            <span>Min</span>
            <input
              type="number"
              min="0"
              inputMode="decimal"
              value={priceDraft.minPrice}
              onChange={(event) =>
                setPriceDraft((prev) => ({
                  ...prev,
                  minPrice: event.target.value,
                }))
              }
              placeholder="0"
            />
          </label>

          <label className="lux-filter-bar__field">
            <span>Max</span>
            <input
              type="number"
              min="0"
              inputMode="decimal"
              value={priceDraft.maxPrice}
              onChange={(event) =>
                setPriceDraft((prev) => ({
                  ...prev,
                  maxPrice: event.target.value,
                }))
              }
              placeholder="500"
            />
          </label>
        </div>
      </div>

      <div className="lux-filter-bar__menu-actions">
        <button
          type="button"
          className="lux-filter-bar__ghost-btn"
          onClick={clearPrice}
        >
          Reset
        </button>
        <button
          type="button"
          className="lux-filter-bar__solid-btn"
          onClick={handlePriceApply}
        >
          Apply
        </button>
      </div>
    </div>
  );

  const renderSortMenu = () => (
    <div className="lux-filter-bar__menu lux-filter-bar__menu--sort">
      <div className="lux-filter-bar__menu-header">
        <span className="lux-filter-bar__eyebrow">Arrange by</span>
        <strong>Sort products</strong>
      </div>

      <div className="lux-filter-bar__option-list">
        {SORT_OPTIONS.map((option) => {
          const active = selectedSort.value === option.value;

          return (
            <button
              key={option.value}
              type="button"
              className={clsx("lux-filter-bar__option-row", {
                "is-active": active,
              })}
              onClick={() => {
                onFilterChange("sort", option.value);
                closePanel();
              }}
            >
              <span>{option.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderOpenMenu = () => {
    const facetConfig = FACET_FILTERS.find((item) => item.key === openMenu);

    if (facetConfig) {
      return renderFacetMenu(facetConfig.key, facetConfig.title);
    }

    if (openMenu === "price") return renderPriceMenu();
    if (openMenu === "sort") return renderSortMenu();

    return null;
  };

  return (
    <section className="lux-filter-bar" ref={rootRef}>
      <div className="lux-filter-bar__surface">
        <div className="lux-filter-bar__line">
          <div className="lux-filter-bar__group lux-filter-bar__group--main">
            {railFilters.map((item) => (
              <div
                key={item.key}
                className={clsx("lux-filter-bar__item", {
                  "is-open": openMenu === item.key,
                })}
              >
                <button
                  type="button"
                  aria-expanded={openMenu === item.key}
                  className={clsx("lux-filter-bar__trigger", {
                    "is-active": item.active,
                    "is-open": openMenu === item.key,
                  })}
                  onClick={() => toggleMenu(item.key)}
                >
                  <span>{item.label}</span>

                  {item.count > 0 ? (
                    <span className="lux-filter-bar__count">{item.count}</span>
                  ) : null}

                  <Chevron open={openMenu === item.key} />
                </button>

                {openMenu === item.key ? (
                  <div className="lux-filter-bar__dropdown">
                    {renderOpenMenu()}
                  </div>
                ) : null}
              </div>
            ))}

            <button
              type="button"
              className={clsx("lux-filter-bar__trigger", {
                "is-active": Boolean(filters.inStock),
              })}
              onClick={() => onFilterChange("inStock", !filters.inStock)}
              aria-pressed={Boolean(filters.inStock)}
            >
              <span>In stock</span>

              {filters.inStock ? (
                <span className="lux-filter-bar__count">1</span>
              ) : null}
            </button>

            <div
              className={clsx(
                "lux-filter-bar__item lux-filter-bar__item--sort",
                {
                  "is-open": openMenu === "sort",
                },
              )}
            >
              <button
                type="button"
                aria-expanded={openMenu === "sort"}
                className={clsx("lux-filter-bar__trigger", {
                  "is-active": filters.sort !== "newest",
                  "is-open": openMenu === "sort",
                })}
                onClick={() => toggleMenu("sort")}
              >
                <span>Sort</span>

                {filters.sort && filters.sort !== "newest" ? (
                  <span className="lux-filter-bar__trigger-meta">
                    {selectedSort.label}
                  </span>
                ) : null}

                <Chevron open={openMenu === "sort"} />
              </button>

              {openMenu === "sort" ? (
                <div className="lux-filter-bar__dropdown lux-filter-bar__dropdown--sort">
                  {renderOpenMenu()}
                </div>
              ) : null}
            </div>
          </div>

          {activeFilters.length > 0 ? (
            <button
              type="button"
              className="lux-filter-bar__clear-inline"
              onClick={() => {
                onClearAll();
                closePanel();
              }}
            >
              Clear all
            </button>
          ) : null}
        </div>

        {activeFilters.length > 0 ? (
          <div className="lux-filter-bar__active" aria-label="Active filters">
            {activeFilters.map((item) => (
              <button
                key={item.key}
                type="button"
                className="lux-filter-bar__active-chip"
                onClick={item.onRemove}
              >
                <span>{item.label}</span>
                <span
                  className="lux-filter-bar__active-chip-x"
                  aria-hidden="true"
                >
                  ×
                </span>
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}
